import { KeyboardArrowDown } from '@mui/icons-material';
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';
import CustomDatePicker from 'components/shared/CustomDatePicker/CustomDatePicker';
import CustomTextField from 'components/shared/CustomTextField/CustomTextField';
import dayjs, { Dayjs } from 'dayjs';
import { useFormikContext } from 'formik';
import { apiDealTypes, FieldConfig } from 'model/deals.model';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DealsService } from 'service/deals.service';
import zeplinColor from 'theme/Color';
import { additionalInformationFields, getDealTypeKey } from '../../DynamicFormSection/utils';

interface DropdownOptions {
  outcome?: string[];
  lossReason?: string[];
  passReason?: string[];
}

const baseFields = (dropdowns: DropdownOptions = {}) => {
  const updatedStaticFields = additionalInformationFields.map((field) => {
    if (field.type === 'select') {
      let options: string[] = [];

      switch (field.name) {
        case 'dealDetails.outcome':
          options = dropdowns.outcome
            ? [...dropdowns.outcome]
            : field.options
              ? Array.from(field.options)
              : [];
          break;
        case 'dealDetails.lossReason':
          options = dropdowns.lossReason
            ? [...dropdowns.lossReason]
            : field.options
              ? Array.from(field.options)
              : [];
          break;
        case 'dealDetails.passReason':
          options = dropdowns.passReason
            ? [...dropdowns.passReason]
            : field.options
              ? Array.from(field.options)
              : [];
          break;
        default:
          options = field.options ? Array.from(field.options) : [];
      }
      return {
        ...field,
        options,
      };
    }
    return field;
  });

  return [...updatedStaticFields];
};

const AdditionalInformation = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xl'));

  const { values, handleChange, handleBlur, setFieldValue } = useFormikContext<any>();
  const details = values.dealDetails || {};
  const pipelineType = values.pipelineType || '';

  const [dropdowns, setDropdowns] = useState<DropdownOptions>({});

  const fetchDropdowns = useCallback(async (dealTypeKey: apiDealTypes) => {
    try {
      const dropdownsData = await DealsService.getDealDropdowns(dealTypeKey);

      const options: DropdownOptions = {};

      if (dropdownsData.outcome && Array.isArray(dropdownsData.outcome)) {
        options.outcome = [...dropdownsData.outcome];
      }
      if (dropdownsData.lossReason && Array.isArray(dropdownsData.lossReason)) {
        options.lossReason = [...dropdownsData.lossReason];
      }
      if (dropdownsData.passReason && Array.isArray(dropdownsData.passReason)) {
        options.passReason = [...dropdownsData.passReason];
      }
      setDropdowns(options);
    } catch (error) {
      console.error('Failed to fetch shared dropdown values:', error);
      setDropdowns({});
    }
  }, []);

  const formFields = useMemo(() => baseFields(dropdowns), [dropdowns]);

  useEffect(() => {
    if (pipelineType) {
      const dealTypeKey = getDealTypeKey(pipelineType);
      if (dealTypeKey) {
        fetchDropdowns(dealTypeKey);
      }
    }
  }, [pipelineType, fetchDropdowns]);

  const renderField = (f: FieldConfig) => {
    const raw = details[f.name.replace('dealDetails.', '')];
    const value = raw ?? (f.type === 'number' ? '' : '');

    if (f.type === 'date') {
      let dateValue: Dayjs | null = null;
      if (raw) {
        if (dayjs.isDayjs && dayjs.isDayjs(raw)) {
          dateValue = raw;
        } else if (raw instanceof Date) {
          dateValue = dayjs(raw);
        } else if (typeof raw === 'string') {
          dateValue = dayjs(raw);
        } else {
          dateValue = dayjs(raw);
        }
      }

      const dateSetter = ((newValue: Dayjs | null | ((prev: Dayjs | null) => Dayjs | null)) => {
        const actualValue = typeof newValue === 'function' ? newValue(dateValue) : newValue;
        const formattedValue = actualValue ? actualValue.toDate() : null;
        setFieldValue(f.name, formattedValue);
      }) as React.Dispatch<React.SetStateAction<Dayjs | null>>;

      return <CustomDatePicker value={dateValue} setValue={dateSetter} label={f.label} />;
    }

    if (f.type === 'select' && f.options) {
      return (
        <FormControl fullWidth sx={{ width: '100%', minWidth: 0 }}>
          <InputLabel
            size={isXs ? 'small' : 'medium'}
            sx={{
              fontSize: isXs ? 14 : 16,
              '& .MuiFormLabel-asterisk': {
                color: zeplinColor.Error,
              },
            }}
            required={f.required}
          >
            {f.label}
          </InputLabel>
          <Select
            IconComponent={KeyboardArrowDown}
            required={f.required}
            name={f.name}
            value={value}
            label={f.label}
            onChange={handleChange}
            onBlur={handleBlur}
            size={isXs ? 'small' : 'medium'}
            sx={{
              width: '100%',
              minWidth: 0,
              '& .MuiSelect-select': {
                fontSize: isXs ? 14 : 16,
              },
            }}
            endAdornment={
              value && (
                <IconButton
                  size='small'
                  sx={{ marginRight: 2, visibility: value ? 'visible' : 'hidden' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleChange({ target: { name: f.name, value: '' } });
                  }}
                >
                  <ClearIcon fontSize='small' />
                </IconButton>
              )
            }
          >
            {f.options.map((opt) => (
              <MenuItem key={String(opt)} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    return (
      <CustomTextField
        required={f.required}
        style={{ width: '100%' }}
        name={f.name}
        label={f.label}
        type={f.type === 'number' ? 'number' : 'text'}
        text={value ?? ''}
        onChange={handleChange}
        onBlur={() => handleBlur(f.name as any)}
        multiline={f.multiline}
        form={true}
      />
    );
  };

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2, width: '100%' }}>
      {formFields.map((f, i) => (
        <Box
          key={f.label + i}
          sx={{ gridColumn: { xs: 'span 12', sm: `span ${f.sm ?? f.xs ?? 12}` } }}
        >
          {renderField(f)}
        </Box>
      ))}
    </Box>
  );
};

export default AdditionalInformation;
